import React, { useEffect, useRef, useState } from 'react';
import {
  extractApiErrorMessage,
  fileToBase64,
  formatBytes,
  getBucketUrlImageV2,
  getImageDimensions,
  uploadTempFile,
} from '@/utils';
import {
  showSuccessToast,
  showErrorToast,
  showWarningToast,
} from '@/components/toast/globalToast';
import { v4 as uuidv4 } from 'uuid';
import FilePreviewCard from '@/components/FilePreviewCard/FilePreviewCard';

interface ImageUploadProps {
  /** Uploaded image URL — a temp-bucket URL after upload, or the stored URL in edit mode. Empty string when nothing is set. */
  value: string;
  /** Called after a successful upload with the temp-bucket URL, the original file name and its size in bytes. */
  onUploaded: (url: string, fileName: string, fileSize: number) => void;
  /** Called when the user clears the image. */
  onRemove: () => void;
  /**
   * Shared-upload image_type enum value. The upstream service only accepts a
   * fixed set (thumbnail, vehicle, vehicleHandover, maintenance, …), so callers
   * pick the closest existing one.
   */
  imageType?: string;
  /** Prompt shown in the empty state. */
  title?: string;
  /**
   * Recommended square edge in px. Off-spec images still upload — the control
   * only warns, since this is guidance rather than a rule. Shorthand for
   * `recommendedDimensions` with equal sides.
   */
  recommendedSize?: number;
  /**
   * Recommended dimensions for non-square art (e.g. a 1024x512 landscape
   * package banner). Takes precedence over `recommendedSize`.
   */
  recommendedDimensions?: { width: number; height: number };
  maxSizeBytes?: number;
  /** Validation error message to render under the control. */
  error?: string;
  /** Fires on upload start/finish so the parent can block submit while in flight. */
  onUploadingChange?: (isUploading: boolean) => void;
  disabled?: boolean;
  className?: string;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const DEFAULT_MAX_SIZE_BYTES = 5 * 1024 * 1024;

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

/** "1024x512 px (2:1)" — the ratio is reduced so it reads as a shape, not a size. */
const describeDimensions = ({
  width,
  height,
}: {
  width: number;
  height: number;
}): string => {
  const divisor = gcd(width, height) || 1;
  return `${width}x${height} px (${width / divisor}:${height / divisor})`;
};

/**
 * Direct image upload control — the image sibling of `PdfUpload`. The file is
 * pushed to the shared temp bucket and the resulting URL is handed back via
 * `onUploaded`; the backend relocates it into its entity-scoped bucket on save.
 */
const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onUploaded,
  onRemove,
  imageType = 'vehicle',
  title = 'Add an image',
  recommendedSize,
  recommendedDimensions,
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  error,
  onUploadingChange,
  disabled = false,
  className = '',
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [localFile, setLocalFile] = useState<{
    name: string;
    size: number;
  } | null>(null);
  // Object URL for the picked file — cheap to hold, unlike a multi-MB data URL.
  const [objectUrl, setObjectUrl] = useState<string>('');
  const [notice, setNotice] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  // One shape for both props — `recommendedSize` is just the square case.
  const recommended =
    recommendedDimensions ??
    (recommendedSize
      ? { width: recommendedSize, height: recommendedSize }
      : null);
  const recommendedLabel = recommended ? describeDimensions(recommended) : '';

  // Revoke on unmount / whenever it is replaced, so blobs don't accumulate.
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const setUploading = (uploading: boolean) => {
    setIsUploading(uploading);
    onUploadingChange?.(uploading);
  };

  const handleSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Allow re-selecting the same file after a remove.
    event.target.value = '';
    if (!file) return;

    // Validate before reading the file: a rejected pick costs nothing.
    if (!ALLOWED_TYPES.includes(file.type)) {
      showErrorToast('Error', 'Image must be a JPG, PNG, or WebP file');
      return;
    }
    if (file.size > maxSizeBytes) {
      showErrorToast(
        'Error',
        `Image must be under ${formatBytes(maxSizeBytes)}`
      );
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    let sizeNotice = '';
    if (recommended) {
      const dimensions = await getImageDimensions(previewUrl);
      if (
        dimensions &&
        (dimensions.width !== recommended.width ||
          dimensions.height !== recommended.height)
      ) {
        sizeNotice = `Uploaded at ${dimensions.width}x${dimensions.height}. ${recommendedLabel} looks best.`;
      }
    }

    try {
      setUploading(true);

      const bucketRes = await getBucketUrlImageV2({
        uuid: uuidv4(),
        type: imageType,
      });
      if (!bucketRes?.data) {
        throw new Error('Failed to get upload URL');
      }
      const url = bucketRes.data;
      const base64Data = await fileToBase64(file);
      const uploadRes = await uploadTempFile(
        url,
        base64Data,
        file.name,
        file.type
      );
      if (uploadRes.status !== 200) {
        throw new Error('Upload failed');
      }

      setObjectUrl(previewUrl);
      setLocalFile({ name: file.name, size: file.size });
      setNotice(sizeNotice);
      onUploaded(url, file.name, file.size);

      if (sizeNotice) {
        showWarningToast('Uploaded', sizeNotice);
      } else {
        showSuccessToast('Success', 'Upload image success');
      }
    } catch (err) {
      URL.revokeObjectURL(previewUrl);
      showErrorToast(
        'Error',
        extractApiErrorMessage(err, 'Failed to upload image')
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setObjectUrl('');
    setLocalFile(null);
    setNotice('');
    onRemove();
  };

  // Prefer the picked file's own preview; fall back to the stored URL (edit mode).
  const previewSrc = objectUrl || value;
  const displayName =
    localFile?.name ||
    (value ? decodeURIComponent(value.split('/').pop() || 'image') : '');

  return (
    <div className={className}>
      <div className="border-2 border-dashed border-gray-300 rounded-lg w-full">
        {isUploading ? (
          <div className="p-8 flex flex-col justify-center items-center text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            <p className="mt-2 text-sm text-gray-600">Uploading image...</p>
          </div>
        ) : !value ? (
          <div className="p-8 flex flex-col justify-center items-center text-center">
            <div className="text-gray-400 text-4xl mb-4">
              <i className="ri-image-line" />
            </div>
            <p className="font-semibold text-gray-700">{title}</p>
            <p className="text-xs text-gray-500 mt-1">
              {recommendedLabel ? `Rekomendasi ${recommendedLabel} · ` : ''}
              JPG, PNG, atau WebP · maks {formatBytes(maxSizeBytes)}
            </p>
            <button
              type="button"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
              className="mt-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Browse File
            </button>
          </div>
        ) : (
          <FilePreviewCard
            fileName={displayName}
            fileSize={localFile?.size}
            leading={
              <img
                src={previewSrc}
                alt={displayName || 'Image'}
                className="h-16 w-16 rounded-md object-cover bg-gray-100 border border-gray-200 flex-shrink-0"
              />
            }
            onReplace={disabled ? undefined : () => inputRef.current?.click()}
            onRemove={disabled ? undefined : handleRemove}
            className="border-0"
          />
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        className="hidden"
        onChange={handleSelect}
      />
      {notice && <p className="text-xs text-amber-600 mt-2">{notice}</p>}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default ImageUpload;
